import { useState, useEffect } from "react";
import { useNotification } from "./useNotification";
import { useApiErrorHandler } from "./useApiErrorHandler";
import useTokenRefresh from "./useTokenRefresh";
import httpclient from "../Utils";

export const useBreakReason = (options = {}) => {
  const {
    autoFetch = true, // Whether to fetch automatically on mount
    pagination = { page: 0, pageSize: 20 }, // Pagination settings
    sorting = [],
    transformData = null, // Optional data transformation function
  } = options;

  const [breakReasons, setBreakReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rowCount, setRowCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();

  const fetchBreakReasons = async (customParams = {}) => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters for pagination, sorting, filtering
      const params = {
        page: pagination.page + 1, // Convert to 1-based indexing
        per_page: pagination.pageSize,
        ...customParams,
      };

      // Add sorting parameters if provided
      if (sorting.length > 0) {
        const sortField = sorting[0].field;
        const sortOrder = sorting[0].sort;
        params.sort_by = sortField;
        params.sort_order = sortOrder;
      }

      const res = await httpclient.get("/break-reason", { params });
      const { data } = res;

      if (data.status === 200) {
        const reasons = transformData ? transformData(data.data) : data.data;
        setBreakReasons(reasons);
        setRowCount(data.meta?.total || data.count || 0);
      } else {
        const errorMsg = data.message || "Failed to fetch break reasons";
        setError(errorMsg);
        showNotification("error", errorMsg);
      }
    } catch (error) {
      const errorMsg = "Failed to fetch break reasons";
      setError(errorMsg);

      if (error.response?.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch on mount and when refresh is triggered
  useEffect(() => {
    if (autoFetch) {
      fetchBreakReasons();
    }
  }, [refreshTrigger, pagination.page, pagination.pageSize, JSON.stringify(sorting)]);

  // Manual refresh function
  const refreshBreakReasons = (params = {}) => {
    if (autoFetch) {
      setRefreshTrigger((prev) => prev + 1);
    } else {
      fetchBreakReasons(params);
    }
  };

  // Function to add a new break reason
  const addBreakReason = async (reasonData) => {
    try {
      setLoading(true);
      // Using the correct endpoint with checkError=1 parameter
      const res = await httpclient.post("/break-reason/create?checkError=1", reasonData);
      const { data } = res;

      if (data.status === 200) {
        showNotification("success", data.message);
        refreshBreakReasons();
        return { success: true, data: data.data };
      } else {
        showNotification("error", data.message);
        return { success: false, error: data.message };
      }
    } catch (error) {
      handleApiError(error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  // Function to update a break reason
  const updateBreakReason = async (id, reasonData) => {
    try {
      setLoading(true);
      const res = await httpclient.post(`/break-reason/update/${id}?checkError=1`, reasonData);
      const { data } = res;

      if (data.status === 200) {
        showNotification("success", data.message);
        refreshBreakReasons();
        return { success: true, data: data.data };
      } else {
        showNotification("error", data.message);
        return { success: false, error: data.message };
      }
    } catch (error) {
      handleApiError(error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  // Function to change status of a break reason
  const changeStatus = async (id) => {
    try {
      setLoading(true);
      const res = await httpclient.post(`/break-reason/status/${id}`);
      const { data } = res;

      if (data.status === 200) {
        showNotification("success", data.message);
        refreshBreakReasons();
        return { success: true, data: data.data };
      } else {
        showNotification("error", data.message);
        return { success: false, error: data.message };
      }
    } catch (error) {
      handleApiError(error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  return {
    breakReasons,
    loading,
    error,
    rowCount,

    fetchBreakReasons,
    refreshBreakReasons,
    addBreakReason,
    updateBreakReason,
    changeStatus,
  };
};